<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Booster Driver Logs</h3>
            </v-card-title>
            <v-card-text>
              <v-tabs v-model="activeTab" color="#e60040" @change="onTabChange">
                <v-tab>Driver</v-tab>
                <v-tab>Salesman</v-tab>
              </v-tabs>
              <v-row class="mt-2">
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search ... "
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  >
                  </v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="statuses"
                    clearable
                    @click:clear="(filter.is_active = null), (page = 1)"
                    label="Status"
                    @input="getData"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3" v-if="activeTab === 0">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.status"
                    :items="driverStatuses"
                    clearable
                    @click:clear="(filter.status = null), (page = 1)"
                    label="Driver Status"
                    @input="getData"
                    prepend-inner-icon="mdi-check-circle"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>

              <!-- Driver Tab Table -->
              <v-simple-table v-else-if="activeTab === 0" class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr. No.</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">Salesman</th>
                      <th class="text-left">Salesman Phone</th>
                      <th class="text-left">Driver</th>
                      <th class="text-left">Driver Phone</th>
                      <th class="text-left">Driver Status</th>
                      <th class="text-left">Expiry Date</th>
                      <th class="text-left">Created Date</th>
                    </tr>
                  </thead>
                  <tbody v-if="booster_driver_logs && booster_driver_logs.length != 0">
                    <tr
                      v-for="(log, i) in booster_driver_logs"
                      :key="`log_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                        'table-row-last': booster_driver_logs.length - 1 == i,
                      }"
                    >
                      <td>{{ i + 1 }}</td>
                      <td>
                        <v-tooltip bottom v-if="log.is_active == 1">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="teal"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-checkbox-marked-circle</v-icon>
                              </v-avatar>
                              Active
                            </v-chip>
                          </template>
                          <span>Active</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="log.is_active == 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-close-circle</v-icon>
                              </v-avatar>
                              Inactive
                            </v-chip>
                          </template>
                          <span>Inactive</span>
                        </v-tooltip>
                      </td>
                      <td>
                        {{ log.salesman ? log.salesman.first_name + ' ' + (log.salesman.last_name || '') : "" }}
                      </td>
                      <td>
                        {{ log.salesman ? log.salesman.phone : "" }}
                      </td>
                      <td>
                        {{ log.driver ? log.driver.first_name + ' ' + (log.driver.last_name || '') : "" }}
                      </td>
                      <td>
                        {{ log.driver ? log.driver.phone : "" }}
                      </td>
                      <td>
                        <v-chip
                          small
                          outlined
                          :color="getDriverStatusColor(log.status)"
                          class="font-weight-medium"
                        >
                          {{ getDriverStatusLabel(log.status) }}
                        </v-chip>
                      </td>
                      <td>{{ log.expiry_date || "" }}</td>
                      <td>{{ log.created_at || "" }}</td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="9" class="text-center">No data found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- Salesman Tab Table -->
              <v-simple-table v-else class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr. No.</th>
                      <th class="text-left">Salesman</th>
                      <th class="text-left">Salesman Phone</th>
                      <th class="text-left">Booster Driver Count</th>
                      <th class="text-left">Total Pending</th>
                      <th class="text-left">Total Completed</th>
                      <th class="text-left">Total Rejected</th>
                      <th class="text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody v-if="salesmanSummaryList && salesmanSummaryList.length != 0">
                    <tr
                      v-for="(group, i) in salesmanSummaryList"
                      :key="`salesman_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                        'table-row-last': salesmanSummaryList.length - 1 == i,
                      }"
                    >
                      <td>{{ i + 1 }}</td>
                      <td>{{ group.salesman ? group.salesman.first_name + ' ' + (group.salesman.last_name || '') : '' }}</td>
                      <td>{{ group.salesman ? group.salesman.phone : '' }}</td>
                      <td>
                        <v-chip small color="blue darken-1" text-color="white">
                          {{ group.driver_count }}
                        </v-chip>
                      </td>
                      <td>
                        <v-chip small outlined color="#F57C00">
                          {{ group.pending_count }}
                        </v-chip>
                      </td>
                      <td>
                        <v-chip small outlined color="#388E3C">
                          {{ group.completed_count }}
                        </v-chip>
                      </td>
                      <td>
                        <v-chip small outlined color="#D32F2F">
                          {{ group.rejected_count }}
                        </v-chip>
                      </td>
                      <td>
                        <v-btn
                          small
                          color="#e60040"
                          dark
                          rounded
                          @click="openDriversDialog(group)"
                        >
                          <v-icon left small>mdi-account-group</v-icon>
                          Booster Drivers
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="8" class="text-center">No data found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <v-pagination
                v-if="activeTab === 0"
                rounded
                v-model="page"
                :length="paginationLength"
              ></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Booster Drivers Dialog -->
      <v-dialog v-model="driversDialog" max-width="850" scrollable>
        <v-card rounded="xl">
          <v-card-title class="d-flex justify-space-between align-center py-4 px-6">
            <span class="font-weight-bold text-h6">
              Booster Drivers &mdash; {{ selectedSalesman ? selectedSalesman.salesmanName : '' }}
            </span>
            <v-btn icon @click="driversDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-4">
            <section v-if="dialogLoading" style="height: 150px">
              <v-progress-linear indeterminate color="primary"></v-progress-linear>
            </section>
            <v-simple-table v-else class="striped-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Sr. No.</th>
                    <th class="text-left">Driver</th>
                    <th class="text-left">Driver Phone</th>
                    <th class="text-left">Driver Status</th>
                    <th class="text-left">Expiry Date</th>
                    <th class="text-left">Created Date</th>
                  </tr>
                </thead>
                <tbody v-if="dialogDrivers && dialogDrivers.length > 0">
                  <tr
                    v-for="(log, i) in dialogDrivers"
                    :key="`dialog_log_${i}`"
                    :class="{
                      'table-row-even': i % 2 === 0,
                      'table-row-odd': i % 2 !== 0,
                    }"
                  >
                    <td>{{ i + 1 }}</td>
                    <td>{{ log.driver ? log.driver.first_name + ' ' + (log.driver.last_name || '') : "" }}</td>
                    <td>{{ log.driver ? log.driver.phone : "" }}</td>
                    <td>
                      <v-chip
                        small
                        outlined
                        :color="getDriverStatusColor(log.status)"
                        class="font-weight-medium"
                      >
                        {{ getDriverStatusLabel(log.status) }}
                      </v-chip>
                    </td>
                    <td>{{ log.expiry_date || "" }}</td>
                    <td>{{ log.created_at || "" }}</td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="6" class="text-center">No drivers found</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "BoosterDriverLogsPage",
  data() {
    return {
      activeTab: 0,
      filter: {
        search_keyword: "",
        is_active: "",
        status: "",
      },
      booster_driver_logs: [],
      salesmanSummaryList: [],
      statuses: [
        {
          id: 0,
          text: "Select Active Status",
          value: "",
        },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" },
      ],
      driverStatuses: [
        { id: 0, text: "All Status", value: "" },
        { id: 1, text: "Pending", value: "0" },
        { id: 2, text: "Approved", value: "1" },
        { id: 3, text: "Rejected", value: "2" },
      ],
      page: 1,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      driversDialog: false,
      selectedSalesman: null,
      dialogDrivers: [],
      dialogLoading: false,
    };
  },
  watch: {
    page() {
      if (this.activeTab === 0) this.getData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    onTabChange() {
      this.page = 1;
      this.getData();
    },
    async getData() {
      this.isLoading = true;
      const searchKeyword = this.filter.search_keyword || "";
      const isActive = this.filter.is_active || "";
      const status = this.filter.status || "";

      if (this.activeTab === 0) {
        let query =
          "page=" +
          this.page +
          "&rowsPerPage=" +
          this.rowsPerPage +
          "&is_active=" +
          isActive +
          "&status=" +
          status +
          "&search_keyword=" +
          searchKeyword;

        await axios
          .get(`driver_booster_logs?${query}`)
          .then((response) => {
            this.booster_driver_logs = response.data.data;
            this.count = response.data.count;
            this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
          })
          .catch(function (error) {
            console.log(error);
          });
      } else {
        let query =
          "is_active=" + isActive + "&search_keyword=" + searchKeyword;

        await axios
          .get(`driver_booster_logs/salesman_summary?${query}`)
          .then((response) => {
            this.salesmanSummaryList = response.data.data;
          })
          .catch(function (error) {
            console.log(error);
          });
      }

      this.isLoading = false;
    },
    async openDriversDialog(group) {
      this.selectedSalesman = group;
      this.dialogDrivers = [];
      this.driversDialog = true;
      this.dialogLoading = true;

      await axios
        .get(`driver_booster_logs/salesman_drivers/${group.salesman_id}`)
        .then((response) => {
          this.dialogDrivers = response.data.data;
        })
        .catch(function (error) {
          console.log(error);
        });

      this.dialogLoading = false;
    },
    getDriverStatusLabel(status) {
      const statuses = {
        0: "Pending",
        1: "Approved",
        2: "Rejected",
      };
      return statuses[status] || "Unknown";
    },
    getDriverStatusColor(status) {
      const colors = {
        0: "#F57C00",
        1: "#388E3C",
        2: "#D32F2F",
      };
      return colors[status] || "#757575";
    },
  },
};
</script>
